import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  IconButton,
  Alert,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import LockIconWrapper from '../components/LockIconWrapper';

// German currency formatting for the amount to be collected at the counter.
const formatMoney = amount =>
  `${(Number(amount) || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

// Confirmation dialog for handing a ready food order over to the guest, opened from
// the kitchen view's "Abgeholt" action. Mirrors the booking PaymentDialog style so
// collecting a food payment feels identical to confirming a booking payment.
//
// - Already-paid (online) orders: a plain pickup confirmation ("Abgeholt bestätigen").
// - Company-invoice orders: nothing to collect — the positions are already on the
//   booking, so saying "bereits bezahlt" here would be wrong.
// - Room-bound orders: not collected at all; the dialog names the room instead.
// - Pay-on-site orders: staff must confirm they collected the counter payment
//   ("kassiert") before the order can leave the pickup queue. The confirm button uses
//   a short two-step countdown (click to arm, click again within 3s) as an extra
//   guard so a cash collection is never confirmed by a single accidental click.
function KitchenPickupDialog({ open, order, onClose, onConfirm, isBusy = false }) {
  const requiresPayment = !!order?.payOnSite;
  const isDelivery = order?.deliveryMode === 'deliverToResource';
  const isCompanyInvoice = order?.paymentType === 'companyInvoice';
  const [countdown, setCountdown] = useState(0);
  const countdownStartRef = useRef(null);

  // Reset the arming countdown whenever the dialog closes so the next order starts
  // from a clean state.
  useEffect(() => {
    if (!open) {
      setCountdown(0);
      countdownStartRef.current = null;
    }
  }, [open]);

  // Tick the arming countdown down to zero (mirrors the booking payment countdown).
  useEffect(() => {
    if (countdown <= 0) return undefined;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - countdownStartRef.current) / 1000);
      const remaining = Math.max(0, 3 - elapsed);
      setCountdown(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, [countdown]);

  if (!order) return null;

  const handleConfirmClick = () => {
    // Already-paid orders confirm immediately. Pay-on-site collection requires two
    // clicks: the first arms the 3s countdown, the second confirms while it runs.
    if (!requiresPayment || countdown > 0) {
      onConfirm(order);
      return;
    }
    countdownStartRef.current = Date.now();
    setCountdown(3);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='body1' sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
            {requiresPayment
              ? 'Speisenabholung kassieren'
              : isDelivery
                ? 'Übergabe im Raum bestätigen'
                : 'Abholung bestätigen'}{' '}
            – #
            {order.orderNumber}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isDelivery && (
          <Alert severity='info' sx={{ mb: 2 }}>
            Diese Bestellung wird nicht abgeholt: Sie gehört zu{' '}
            <strong>{order.resourceName || 'einem Raum'}</strong> und wird dorthin gebracht.
          </Alert>
        )}

        {requiresPayment ? (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Diese Bestellung wurde noch nicht bezahlt. Bitte kassiere den Betrag vor der Übergabe.
          </Alert>
        ) : isCompanyInvoice ? (
          <Alert severity='success' sx={{ mb: 2 }}>
            Läuft über die Firmenrechnung
            {order.companyBooking?.companyName ? ` von ${order.companyBooking.companyName}` : ''}
            {order.companyBooking?.bookingNumber
              ? ` (Buchung ${order.companyBooking.bookingNumber})`
              : ''}
            . Hier ist nichts zu kassieren.
          </Alert>
        ) : (
          <Alert severity='success' sx={{ mb: 2 }}>
            Diese Bestellung ist bereits bezahlt. Bitte nur die Übergabe an den Gast bestätigen.
          </Alert>
        )}

        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {(order.items || []).map((item, i) => (
            <Typography
              key={i}
              variant='body2'
              sx={{
                fontWeight: item.isPizza ? 700 : 400,
                color: item.isPizza ? 'secondary.main' : 'text.primary',
              }}
            >
              {item.count}× {item.name}
            </Typography>
          ))}
        </Stack>

        <Divider />

        <Box display='flex' justifyContent='center' alignItems='center' mt={2}>
          <Box p={2} borderRadius={2} textAlign='center'>
            {requiresPayment && (
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>
                <b>Zu kassieren:</b> {formatMoney(order.totalAmount)}
              </Typography>
            )}
            <Box display='flex' justifyContent='center' alignItems='center' mt={2}>
              <Button
                variant='contained'
                color='success'
                startIcon={requiresPayment ? <PaymentsIcon /> : undefined}
                onClick={handleConfirmClick}
                disabled={isBusy}
              >
                <LockIconWrapper right={'foodOrder:update'}>
                  {requiresPayment
                    ? countdown > 0
                      ? `Kassiert & abgeholt? (${countdown}s)`
                      : 'Kassiert & abgeholt'
                    : isDelivery
                      ? 'Gebracht bestätigen'
                      : 'Abgeholt bestätigen'}
                </LockIconWrapper>
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button sx={{ color: 'red' }} onClick={onClose} disabled={isBusy}>
          Abbrechen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default KitchenPickupDialog;
